import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Router } from "aurelia-router";
import { bindable } from "aurelia-templating";
import { RightShipQiRouterConstants } from "../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../constants/authentication/claims";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { RightshipEexiComplianceTypeEnum, VesselHeaderModel } from "../../../models/vessels/vessel.models";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../services/basics/signal-functions";
import { ManageVesselService } from "../../../services/vessels/manage-vessel.service";
import { ClipboardCopyService } from "../../../utilities/clipboard-copy-service";
import { Reflection } from "../../../utilities/reflection";

@autoinject
@transient()
export class PartialVesselHeader {

    model: VesselHeaderModel;
    event: Subscription;
    canEditVesselDetails: boolean;
    canRequestRightShipInspection: boolean;
    canAccessVesselComparison: boolean;
    showEexiCompliantNotification: boolean;

    @bindable()
    vesselId: number;

    @bindable()
    isEditableVesselName: boolean = false;

    @bindable()
    forceLoad: boolean = false;

    @bindable
    showGrading: boolean = false;

    @bindable
    showVesselComparisonLink: boolean = false;

    @bindable
    redirectToVessel: boolean = true;

    @bindable
    showDwtInfo: boolean = true;

    @bindable
    signalRGrouping: boolean = true;

    @bindable
    showVesselDetails: boolean;

    constructor(private vesselService: ManageVesselService, private clipboardCopyService: ClipboardCopyService,
        userClaimsService: UserClaimsService, private router: Router,
        private signalClientService: SignalClientService, private eventAggregator: EventAggregator) {
        this.canEditVesselDetails = userClaimsService.hasPermission(Claims.EditVesselDetails);
        this.canRequestRightShipInspection = userClaimsService.hasPermission(Claims.CanRequestRightShipInspection);
        this.canAccessVesselComparison = userClaimsService.hasPermission(Claims.CanAccessVesselComparison);
        this.showEexiCompliantNotification = userClaimsService.hasPermission(Claims.ShowEEXIComplianceNotification);
    }

    attached() {
        this.loadVesselHeader(this.forceLoad);
        this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        
        if(this.signalRGrouping) {
            this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, 
                SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.rsInspectionRefresh);
        }
        else {
            this.signalClientService.subscribe(SignalFunctions.entityStatusChanged, this.rsInspectionRefresh);
        }

        this.event = this.eventAggregator.subscribe("refresh-vessel-header", this.refreshRating);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        if(this.signalRGrouping) {
            this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, 
                SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.rsInspectionRefresh);
        }
        else {
            this.signalClientService.unsubscribe(SignalFunctions.entityStatusChanged, this.rsInspectionRefresh);
        }

        if (this.event) this.event.dispose();
    }

    private refreshRating = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.vesselId).length >= 1) {
            await this.loadVesselHeader(true);
        }
    }

    @computedFrom('model.isSanctioned', 'model.isInGeoPoliticalZone', 'model.hasGeopoliticalConflict', 'model.isFlagFake')
    get isSanctionedOrGeopoliticalZoneOrFlagFake() {
        if (this.model.isSanctioned || this.model.isFlagFake || this.model.isInGeoPoliticalZone || this.model.hasGeopoliticalConflict) {
            return true;
        }
    }

    private rsInspectionRefresh = async (param: EntityStatusChangeModel) => {
        if (param && param.vesselId && param.vesselId == this.vesselId) {
            await this.loadVesselHeader(true);
        }
    }

    vesselIdChanged() {
        this.loadVesselHeader(false);
    }

    async loadVesselHeader(force: boolean) {
        if (this.vesselId) {
            try {
                this.model = await this.vesselService.getVesselHeader(this.vesselId, force);
            } catch {
                console.error("Could not load vessel header vessel ID " + this.vesselId);
            }
        }
    }

    @computedFrom('model.isEexiCompliant', 'model.eexiComplianceTypeId', 'showEexiCompliantNotification')
    public get showEexiWarning() {
        return this.model.isEexiCompliant != null && this.model.eexiComplianceTypeId != null && 
            this.showEexiCompliantNotification && (this.model.isEexiCompliant == false || this.model.eexiComplianceTypeId == RightshipEexiComplianceTypeEnum.Evdi);
    }

    @computedFrom(Reflection.getPropertyPath<PartialVesselHeader>(v => v.model.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.model.vesselId,
        });
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    navigateToComparisonPage() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselComparison, { vesselId: this.vesselId });
    }
}